import { AppComponent } from "@reporting/rp-app-host";
import { AnalyticsApplication, BaseVisualizationEditor } from "../..";
import { ColorTypes, IconSpec, utils } from "@omegagrid/core";
import { loader as chartsLoader } from "@omegagrid/plugin-charts";
import { ContextData, ContextScript, MultiContextData } from "@reporting/rp-types/analytics";
import { ReportResult } from "../../model";
import rp from "@reporting/rp-app-host";
import * as visualizationModules from '../visualizations';

export class ReportEvent extends Event {
	readonly visualization: BaseVisualization;

	constructor(type: string, args?: Partial<ReportEvent>) {
		super(`report.${type}`, {bubbles: true, composed: true});
		if (args) Object.assign(this, args);
	}
}

export class ReportErrorEvent extends Event {
	constructor(readonly error: Error) {
		super(`report.error`, {bubbles: true, composed: true});
	}
}

export type ExtensionContext = {
	component: BaseVisualization;
	contextData: ContextData;
	reportData: ReportResult;
	visualizations: typeof visualizationModules;
	charts: typeof chartsLoader;
}

export type ExportType = {
	type: 'xlsx' | 'csv' | 'pdf' | string,
	icon?: string|IconSpec,
	/** menu item text, defaults to `Export to ${type}` */
	label?: string,
}

export type VisualizationAction = {
	id: string,
	text: string,
	color?: ColorTypes,
	icon?: string,
}


export interface VisualizationOptions {
	save?: boolean;
	saveAs?: boolean;
	addTo?: string[];
	refresh?: boolean;
	exportTypes?: ExportType[];
	actions?: VisualizationAction[];
	edit?: boolean;
	/** items of the "+" toolbar dropdown for adding items into the visualization */
	addMenu?: VisualizationAction[];
}

const DEFAULT_OPTIONS: VisualizationOptions = {
	save: true,
	saveAs: true,
	refresh: true,
	addTo: ['dashboard', 'spreadsheet'],
}

export abstract class BaseVisualization<TData extends ContextData|MultiContextData = ContextData|MultiContextData> extends AppComponent<AnalyticsApplication> {

	reportData: ReportResult;
	contextData: TData;
	caption: string;

	invokeAction(id: string, data?: any) { }

	get itemCount(): number {
		return 1;
	}

	edit() {
		console.warn('Edit method not implemented for this visualization');
	}

	protected createOptions(options: Partial<VisualizationOptions> = {}): VisualizationOptions {
		return utils.mergeDeep(DEFAULT_OPTIONS, options);
	}

	getOptions(): VisualizationOptions|Promise<VisualizationOptions> {
		return this.createOptions();
	}

	dispatchEditEvent() {
		// this.dispatchEvent(new CustomEvent(this.isSaved ? 'save' : 'edit', {bubbles: true, composed: true}));
		this.dispatchEvent(new CustomEvent('edit', {bubbles: true, composed: true}));
	}

	/**
	 * Visualizations rendering the context - itself for a plain visualization,
	 * the items for a dashboard derived from a single context (e.g. a chart
	 * view split by the chart axis).
	 */
	getVisualizations(): BaseVisualization[] {
		return [this];
	}

	refresh(contextData?: TData) {
		if (contextData) this.contextData = contextData;
	}

	async export(type: string) { }

	async download(type: string) { }

	protected runScript(source: string) {
		const fn = new Function('ctx', 'rp', source);
		const ctx: ExtensionContext = {
			component: this,
			contextData: this.contextData as ContextData,
			reportData: this.reportData,
			visualizations: visualizationModules,
			charts: chartsLoader,
		};
		fn.call(this, ctx, rp);
	}

	private _extensionEventListeners: [string, EventListenerOrEventListenerObject][];

	runScripts() {
		const context = this.contextData as ContextData;

		// clean previous listeners to avoid duplicates when refreshing
		this._extensionEventListeners?.forEach(([type, listener]) => this.removeEventListener(type, listener));

		// catch all event listeners registered by the extension scripts
		this._extensionEventListeners = [];
		const origAddEventListener = this.addEventListener;
		this.addEventListener = (type: string, listener: EventListenerOrEventListenerObject, options?: boolean | AddEventListenerOptions) => {
			origAddEventListener.call(this, type, listener, options);
			this._extensionEventListeners.push([type, listener]);
		};

		// run extension scripts
		(context?.scripts ?? []).forEach(script => this.runScript(script.source));

		this.addEventListener = origAddEventListener;
	}

}
